import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye, EyeOff, Save, ArrowLeft, Monitor, X } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const REGIONS = ['Europe', 'Asia & Pacific', 'Africa & Indian Ocean', 'Americas', 'Middle East'];

function DestinationForm({ id }) {
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [form, setForm] = useState({ destination_name: '', slug: '', hero_image_id: '', short_description: '', region: '', region_tagline: '', order: 0, status: 'draft' });
  const [saving, setSaving] = useState(false);
  const [mediaModal, setMediaModal] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!isNew) {
      axios.get(`${API}/api/cms/destinations/${id}`, { withCredentials: true })
        .then(res => setForm(res.data))
        .catch(() => navigate('/advisor/content/destinations'));
    }
  }, [id, isNew, navigate]);

  const autoSlug = (n) => n.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-');

  const update = (field, val) => {
    setForm(f => {
      const next = { ...f, [field]: val };
      if (field === 'destination_name' && (isNew || f.slug === autoSlug(f.destination_name))) next.slug = autoSlug(val);
      return next;
    });
  };

  const save = async (status) => {
    setSaving(true);
    const body = { ...form, status: status || form.status };
    try {
      if (isNew) {
        await axios.post(`${API}/api/cms/destinations`, body, { withCredentials: true });
      } else {
        await axios.put(`${API}/api/cms/destinations/${id}`, body, { withCredentials: true });
      }
      navigate('/advisor/content/destinations');
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const pickImage = () => {
    setMediaModal(true);
    axios.get(`${API}/api/cms/media?media_type=image`, { withCredentials: true }).then(res => setMediaItems(res.data));
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="destination-editor">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-start justify-between mb-8">
          <Link to="/advisor/content/destinations" className="flex items-center gap-1 text-xs uppercase tracking-[0.15em] text-[#999] hover:text-[#1A1A1A]">
            <ArrowLeft size={12} /> Destinations
          </Link>
          <div className="flex gap-2">
            <button onClick={() => setShowPreview(true)} className="flex items-center gap-1.5 px-4 py-2 text-sm border border-[#E8E4DC] rounded hover:border-[#1A1A1A] transition-colors" data-testid="dest-preview-btn">
              <Monitor size={12} /> Preview
            </button>
            <button onClick={() => save('draft')} disabled={saving} className="px-4 py-2 text-sm border border-[#E8E4DC] rounded hover:border-[#1A1A1A]" data-testid="dest-save-draft">
              <Save size={12} className="inline mr-1" />Save Draft
            </button>
            <button onClick={() => save('published')} disabled={saving} className="px-4 py-2 text-sm bg-[#1A1A1A] text-white rounded hover:bg-[#333]" data-testid="dest-publish">
              <Eye size={12} className="inline mr-1" />Publish
            </button>
          </div>
        </div>

        <div className="space-y-5">
          <input value={form.destination_name} onChange={e => update('destination_name', e.target.value)} placeholder="Destination name" className="w-full font-serif text-2xl bg-transparent border-none outline-none placeholder:text-[#E8E4DC]" data-testid="dest-name-input" />
          <div className="grid grid-cols-2 gap-3">
            <input value={form.slug} onChange={e => update('slug', e.target.value)} placeholder="slug" className="text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white outline-none" data-testid="dest-slug-input" />
            <select value={form.region} onChange={e => update('region', e.target.value)} className="text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white outline-none" data-testid="dest-region-select">
              <option value="">Select region</option>
              {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <input value={form.region_tagline} onChange={e => update('region_tagline', e.target.value)} placeholder="Region tagline (optional)" className="w-full text-sm px-3 py-2 border border-[#E8E4DC] rounded bg-white outline-none" data-testid="dest-tagline-input" />
          <textarea value={form.short_description} onChange={e => update('short_description', e.target.value)} placeholder="Short description" rows={3} className="w-full text-sm px-3 py-2 border border-[#E8E4DC] rounded bg-white outline-none resize-none" data-testid="dest-desc-input" />
          <div className="flex items-center gap-3">
            <button onClick={pickImage} className="text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white hover:border-[#999]" data-testid="dest-image-btn">
              {form.hero_image_id ? 'Change hero image' : 'Select hero image'}
            </button>
            <input value={form.order} onChange={e => update('order', parseInt(e.target.value) || 0)} type="number" className="text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white outline-none w-20" placeholder="Order" data-testid="dest-order-input" />
          </div>
          <input value={form.external_hero_url || ''} onChange={e => update('external_hero_url', e.target.value)} placeholder="External hero image URL (Pexels, Unsplash, etc.)" className="w-full text-xs px-3 py-2 border border-[#E8E4DC] rounded bg-white outline-none" data-testid="dest-external-url-input" />
          {(form.external_hero_url || form.hero_image_id) && (
            <img src={form.external_hero_url || `${API}/api/cms/media/file/${form.hero_image_id}`} alt="Hero" className="w-full h-40 object-cover rounded-lg" />
          )}
        </div>
      </div>

      {showPreview && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto" data-testid="dest-preview-modal">
          <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-3 bg-[#1A1A1A] text-white">
            <div className="flex items-center gap-3">
              <Monitor size={14} />
              <span className="text-xs uppercase tracking-[0.15em]">Destination Preview</span>
              {form.status === 'draft' && <span className="text-[10px] px-2 py-0.5 bg-white/20 rounded-full">Draft</span>}
            </div>
            <button onClick={() => setShowPreview(false)} className="p-1.5 hover:bg-white/10 rounded transition-colors" data-testid="dest-preview-close">
              <X size={16} />
            </button>
          </div>
          <div className="min-h-screen bg-[#FAFAF8]">
            <section className="py-28 sm:py-36 px-6 text-center">
              <p className="text-xs uppercase tracking-[0.15em] text-[#999] mb-6">Curated Destinations</p>
              <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] max-w-2xl mx-auto">Where We Go</h1>
            </section>
            <section className="py-20 px-6 bg-white">
              <div className="max-w-5xl mx-auto">
                <h2 className="font-serif text-base text-[#1A1A1A] mb-1">{form.region || 'Region'}</h2>
                <p className="text-xs text-[#999] mb-6">{form.region_tagline || ''}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="block">
                    {(form.external_hero_url || form.hero_image_id) ? (
                      <img src={form.external_hero_url || `${API}/api/cms/media/file/${form.hero_image_id}`} alt={form.destination_name} className="aspect-[3/2] w-full object-cover rounded mb-3" />
                    ) : (
                      <div className="aspect-[3/2] bg-[#F0EDE6] rounded mb-3" />
                    )}
                    <p className="text-sm text-[#1A1A1A]">{form.destination_name || 'Destination Name'}</p>
                    <p className="text-xs text-[#999] mt-0.5">{form.short_description || 'Short description'}</p>
                  </div>
                  {/* Placeholder cards to show context */}
                  <div className="block opacity-30">
                    <div className="aspect-[3/2] bg-[#F0EDE6] rounded mb-3" />
                    <p className="text-sm text-[#1A1A1A]">Other Destination</p>
                    <p className="text-xs text-[#999] mt-0.5">Another destination in this region</p>
                  </div>
                  <div className="block opacity-30">
                    <div className="aspect-[3/2] bg-[#F0EDE6] rounded mb-3" />
                    <p className="text-sm text-[#1A1A1A]">Other Destination</p>
                    <p className="text-xs text-[#999] mt-0.5">Another destination in this region</p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      )}

      {mediaModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-6" onClick={() => setMediaModal(false)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[70vh] overflow-auto p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-serif text-lg mb-4">Select Image</h3>
            <div className="grid grid-cols-4 gap-3">
              {mediaItems.map(item => (
                <button key={item.id} onClick={() => { update('hero_image_id', item.id); setMediaModal(false); }} className="aspect-square overflow-hidden rounded border border-[#E8E4DC] hover:border-[#1A1A1A]">
                  <img src={`${API}${item.file_url}`} alt={item.original_filename} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
            {mediaItems.length === 0 && <p className="text-sm text-[#999]">No images. Upload media first.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DestinationsCMS() {
  const { id } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      axios.get(`${API}/api/cms/destinations`, { withCredentials: true })
        .then(res => { setItems(res.data); setLoading(false); })
        .catch(() => setLoading(false));
    }
  }, [id]);

  const handleDelete = async (destId) => {
    if (!window.confirm('Delete this destination?')) return;
    await axios.delete(`${API}/api/cms/destinations/${destId}`, { withCredentials: true });
    setItems(prev => prev.filter(i => i.id !== destId));
  };

  if (id) return <DestinationForm id={id} />;

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="destinations-cms">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex items-start justify-between mb-10">
          <div>
            <Link to="/advisor/content" className="text-xs uppercase tracking-[0.15em] text-[#999] hover:text-[#1A1A1A]">Content</Link>
            <h1 className="font-serif text-3xl text-[#1A1A1A] mt-4">Destinations</h1>
          </div>
          <button onClick={() => navigate('/advisor/content/destinations/new')} className="flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] text-white text-sm rounded hover:bg-[#333]" data-testid="dest-new-btn">
            <Plus size={14} /> New Destination
          </button>
        </div>

        {loading ? <p className="text-sm text-[#999]">Loading...</p> : items.length === 0 ? (
          <div className="text-center py-20 border border-dashed border-[#E8E4DC] rounded-lg">
            <p className="text-sm text-[#999]">No destinations yet</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="dest-list">
            {items.map(item => (
              <div key={item.id} className="flex items-center justify-between p-4 bg-white border border-[#E8E4DC] rounded-lg group" data-testid={`dest-item-${item.id}`}>
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center gap-3 mb-0.5">
                    <h3 className="font-serif text-base text-[#1A1A1A]">{item.destination_name}</h3>
                    {item.status === 'published' ? (
                      <span className="text-[10px] text-green-700 bg-green-50 px-2 py-0.5 rounded-full"><Eye size={10} className="inline mr-0.5" />Published</span>
                    ) : (
                      <span className="text-[10px] text-[#999] bg-[#F5F3EE] px-2 py-0.5 rounded-full"><EyeOff size={10} className="inline mr-0.5" />Draft</span>
                    )}
                  </div>
                  <p className="text-xs text-[#999]">{item.region} &middot; {item.short_description}</p>
                </div>
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => navigate(`/advisor/content/destinations/${item.id}`)} className="p-2 text-[#6B6B6B] hover:text-[#1A1A1A]"><Edit2 size={14} /></button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 text-[#6B6B6B] hover:text-red-500"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
